import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { SlidersHorizontal, X, RotateCcw, Search, Car as CarIcon } from "lucide-react";
import api from "@/lib/api";
import CarCard from "@/components/site/CarCard";
import Seo from "@/components/site/Seo";
import { FUELS, TRANSMISSIONS, BODY_TYPES, DRIVETRAINS, SORT_OPTIONS } from "@/lib/format";
import { TID } from "@/constants/tids";

const inp =
  "bg-[#0a0a0a] border border-white/[0.08] rounded-lg h-11 px-3.5 w-full text-white placeholder-white/40 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]/40 text-[13px] outline-none transition-colors";
const lbl =
  "block text-[10px] font-bold uppercase tracking-[0.16em] text-white/50 mb-1.5";

/**
 * Deduplicare mărci: normalizează pe (trim + lowercase, colapsând spații
 * multiple și diacritice), apoi alege pentru fiecare grup varianta cea mai
 * „cursivă" (Title Case tip „Ford" sau acronim tip „BMW"/„VW"). Rezultatul
 * e sortat alfabetic, insensitiv la diacritice (Škoda apare corect după S).
 */
function dedupeBrands(raw) {
  if (!Array.isArray(raw)) return [];
  const groups = new Map(); // normKey -> [variantă1, variantă2, ...]
  for (const item of raw) {
    if (typeof item !== "string") continue;
    const trimmed = item.trim().replace(/\s+/g, " ");
    if (!trimmed) continue;
    // cheie normalizată: lowercase + spații colapsate + fără diacritice
    const key = trimmed
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
    const list = groups.get(key) || [];
    list.push(trimmed);
    groups.set(key, list);
  }

  const pickCanonical = (variants) => {
    // 1) preferă Title Case (majusculă + minuscule) — ex. „Ford", „Škoda"
    const mixed = variants.filter((v) => /[A-Z]/.test(v) && /[a-z]/.test(v));
    if (mixed.length) {
      return mixed.sort((a, b) => {
        // Preferă variante mai scurte
        if (a.length !== b.length) return a.length - b.length;
        // La egalitate, preferă varianta cu diacritice (are caractere non-ASCII)
        const aDia = /[^\x00-\x7F]/.test(a) ? 0 : 1;
        const bDia = /[^\x00-\x7F]/.test(b) ? 0 : 1;
        if (aDia !== bDia) return aDia - bDia;
        return a.localeCompare(b);
      })[0];
    }
    // 2) altfel, acronime cu toate literele mari — ex. „BMW", „VW"
    const upper = variants.filter((v) => v === v.toUpperCase() && /[A-Z]/.test(v));
    if (upper.length) return upper.sort((a, b) => a.localeCompare(b))[0];
    // 3) fallback: Title Case manual pe prima variantă
    const first = variants[0];
    return first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
  };

  const canonical = [];
  for (const variants of groups.values()) {
    canonical.push(pickCanonical(variants));
  }
  return canonical.sort((a, b) =>
    a.localeCompare(b, "ro", { sensitivity: "base" })
  );
}

function FilterFields({ getP, setP, brands }) {
  return (
    <div className="space-y-5">
      <div>
        <label className={lbl}>Marcă</label>
        <select
          data-testid={TID.filterBrand}
          className={inp}
          value={getP("brand")}
          onChange={(e) => setP("brand", e.target.value)}
        >
          <option value="">Toate mărcile</option>
          {brands.map((b) => (
            <option key={b} value={b}>
              {b}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className={lbl}>Caroserie</label>
        <select
          data-testid={TID.filterBody}
          className={inp}
          value={getP("body_type")}
          onChange={(e) => setP("body_type", e.target.value)}
        >
          <option value="">Toate</option>
          {BODY_TYPES.map((b) => (
            <option key={b}>{b}</option>
          ))}
        </select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={lbl}>An de la</label>
          <input
            data-testid={TID.filterYearMin}
            className={inp}
            type="number"
            placeholder="2015"
            value={getP("year_min")}
            onChange={(e) => setP("year_min", e.target.value)}
          />
        </div>
        <div>
          <label className={lbl}>An până la</label>
          <input
            data-testid={TID.filterYearMax}
            className={inp}
            type="number"
            placeholder="2024"
            value={getP("year_max")}
            onChange={(e) => setP("year_max", e.target.value)}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={lbl}>Preț min €</label>
          <input
            data-testid={TID.filterPriceMin}
            className={inp}
            type="number"
            placeholder="0"
            value={getP("price_min")}
            onChange={(e) => setP("price_min", e.target.value)}
          />
        </div>
        <div>
          <label className={lbl}>Preț max €</label>
          <input
            data-testid={TID.filterPriceMax}
            className={inp}
            type="number"
            placeholder="50.000"
            value={getP("price_max")}
            onChange={(e) => setP("price_max", e.target.value)}
          />
        </div>
      </div>
      <div>
        <label className={lbl}>Kilometraj maxim</label>
        <input
          data-testid={TID.filterKmMax}
          className={inp}
          type="number"
          placeholder="200.000"
          value={getP("km_max")}
          onChange={(e) => setP("km_max", e.target.value)}
        />
      </div>
      <div>
        <label className={lbl}>Combustibil</label>
        <select
          data-testid={TID.filterFuel}
          className={inp}
          value={getP("fuel")}
          onChange={(e) => setP("fuel", e.target.value)}
        >
          <option value="">Toate</option>
          {FUELS.map((f) => (
            <option key={f}>{f}</option>
          ))}
        </select>
      </div>
      <div>
        <label className={lbl}>Cutie</label>
        <select
          data-testid={TID.filterTransmission}
          className={inp}
          value={getP("transmission")}
          onChange={(e) => setP("transmission", e.target.value)}
        >
          <option value="">Toate</option>
          {TRANSMISSIONS.map((t) => (
            <option key={t}>{t}</option>
          ))}
        </select>
      </div>
      <div>
        <label className={lbl}>Tracțiune</label>
        <select
          data-testid={TID.filterDrivetrain}
          className={inp}
          value={getP("drivetrain")}
          onChange={(e) => setP("drivetrain", e.target.value)}
        >
          <option value="">Toate</option>
          {DRIVETRAINS.map((d) => (
            <option key={d.value} value={d.value}>
              {d.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

const FILTER_KEYS = [
  "brand",
  "body_type",
  "year_min",
  "year_max",
  "price_min",
  "price_max",
  "km_max",
  "fuel",
  "transmission",
  "drivetrain",
  "q",
];

export default function CarsList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [cars, setCars] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const getP = (k, d = "") => searchParams.get(k) ?? d;
  const setP = (k, v) => {
    const next = new URLSearchParams(searchParams);
    if (v === "" || v == null) next.delete(k);
    else next.set(k, v);
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    api.get("/vehicles/brands").then((r) => setBrands(r.data)).catch(() => {});
  }, []);

  // Deduplicare + normalizare + sortare — indiferent ce returnează backend-ul
  const cleanBrands = useMemo(() => dedupeBrands(brands), [brands]);

  useEffect(() => {
    const params = Object.fromEntries(searchParams.entries());
    setLoading(true);
    api
      .get("/vehicles", { params })
      .then((r) => setCars(r.data))
      .catch(() => setCars([]))
      .finally(() => setLoading(false));
  }, [searchParams]);

  const activeFilterCount = useMemo(
    () => FILTER_KEYS.filter((k) => searchParams.get(k)).length,
    [searchParams],
  );
  const hasActiveFilters = activeFilterCount > 0;

  const reset = () => {
    const sort = searchParams.get("sort");
    const next = new URLSearchParams();
    if (sort) next.set("sort", sort);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="bg-[#0a0a0a] min-h-screen">
      <Seo
        title="Mașini second hand de vânzare în Suceava | Stoc AutoRIO"
        description="Vezi stocul de autoturisme second hand verificate la AutoRIO Suceava. Filtrează după marcă, preț, an și kilometraj. Finanțare, garanție și livrare în toată țara."
        path="/masini"
      />

      {/* Premium header intro */}
      <section className="relative overflow-hidden pt-32 pb-14 sm:pt-40 sm:pb-16 border-b border-white/[0.06]">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(60% 60% at 50% 0%, rgba(212,175,55,0.14) 0%, transparent 60%), radial-gradient(80% 60% at 100% 100%, rgba(212,175,55,0.06) 0%, transparent 60%)",
          }}
        />
        <div className="relative container-wide">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
            className="max-w-3xl text-center mx-auto"
          >
            <div className="inline-flex items-center gap-2 rounded-full bg-white/[0.05] backdrop-blur-md px-4 py-1.5 border border-white/[0.1]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#D4AF37]" />
              <span className="text-[10px] font-bold uppercase tracking-[0.24em] text-white/80">
                Stoc actualizat
              </span>
            </div>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl md:text-6xl font-extrabold text-white leading-[1.05] tracking-tight">
              Stoc{" "}
              <span className="font-serif-italic gold-gradient-text font-medium">
                Auto
              </span>
              RIO
            </h1>
            <p className="mt-5 text-base sm:text-lg text-white/60 leading-relaxed max-w-2xl mx-auto">
              Autoturisme atent selectate, verificate tehnic și pregătite pentru livrare.
            </p>
            <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#D4AF37] border border-[#D4AF37]/30 bg-[#D4AF37]/[0.06] rounded-full px-5 py-2">
              <CarIcon className="h-4 w-4" strokeWidth={2.2} />
              {loading ? (
                <span>Se încarcă stocul...</span>
              ) : (
                <span>
                  <span className="font-extrabold">{cars.length}</span>{" "}
                  {cars.length === 1
                    ? "autoturism disponibil"
                    : "autoturisme disponibile"}
                </span>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Toolbar: sort + mobile filter trigger */}
      <div className="container-wide pt-8 sm:pt-10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMobileFiltersOpen(true)}
              className="lg:hidden inline-flex items-center gap-2 h-11 px-4 rounded-full border border-white/[0.1] bg-white/[0.03] text-white text-sm font-semibold hover:border-[#D4AF37]/50 transition-colors"
            >
              <SlidersHorizontal className="h-4 w-4 text-[#D4AF37]" />
              Filtre
              {hasActiveFilters && (
                <span className="ml-1 h-5 min-w-5 px-1.5 rounded-full bg-[#D4AF37] text-[#0a0a0a] text-[11px] font-bold flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </button>
            {hasActiveFilters && (
              <button
                data-testid={TID.filterReset}
                onClick={reset}
                className="inline-flex items-center gap-1.5 h-11 px-4 rounded-full text-white/70 hover:text-[#D4AF37] text-[13px] font-semibold uppercase tracking-[0.14em] transition-colors"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Resetează filtrele
              </button>
            )}
          </div>

          <div className="flex items-center gap-3">
            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/40">
              Sortează după
            </span>
            <div className="relative">
              <select
                data-testid={TID.filterSort}
                className="appearance-none bg-[#0a0a0a] border border-white/[0.1] rounded-full h-11 pl-5 pr-11 text-[13px] font-semibold text-white hover:border-[#D4AF37]/50 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]/30 outline-none transition-colors cursor-pointer"
                value={getP("sort", "recent")}
                onChange={(e) => setP("sort", e.target.value)}
                aria-label="Sortare"
              >
                {SORT_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
              <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[#D4AF37] text-xs">
                ▼
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Main grid */}
      <div className="container-wide py-8 sm:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-8 lg:gap-10">
          {/* Desktop sidebar */}
          <aside className="hidden lg:block">
            <div className="sticky top-28 rounded-2xl border border-white/[0.08] bg-[#111111] p-6">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <SlidersHorizontal className="h-4 w-4 text-[#D4AF37]" />
                  <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-white">
                    Filtre
                  </div>
                </div>
                {hasActiveFilters && (
                  <span className="h-6 min-w-6 px-2 rounded-full bg-[#D4AF37] text-[#0a0a0a] text-[11px] font-bold flex items-center justify-center">
                    {activeFilterCount}
                  </span>
                )}
              </div>
              <FilterFields getP={getP} setP={setP} brands={cleanBrands} />
              {hasActiveFilters && (
                <button
                  data-testid={`${TID.filterReset}-sidebar`}
                  onClick={reset}
                  className="mt-6 w-full h-11 inline-flex items-center justify-center gap-2 rounded-full border border-white/[0.1] text-white/80 hover:text-[#D4AF37] hover:border-[#D4AF37]/50 text-[12px] font-bold uppercase tracking-[0.16em] transition-colors"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  Resetează filtrele
                </button>
              )}
            </div>
          </aside>

          <div>
            {!loading && cars.length > 0 && (
              <div
                data-testid="results-count"
                className="mb-6 text-sm font-semibold text-white/70"
              >
                {cars.length === 1
                  ? "S-a găsit 1 autoturism"
                  : `S-au găsit ${cars.length} autoturisme`}
              </div>
            )}
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 lg:gap-8">
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className="animate-pulse rounded-[22px] h-[440px] bg-white/[0.03] border border-white/[0.04]"
                  />
                ))}
              </div>
            ) : cars.length === 0 ? (
              <EmptyState onReset={reset} />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 lg:gap-8">
                {cars.map((c, i) => (
                  <CarCard key={c.id} car={c} index={i} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile filters drawer */}
      <AnimatePresence>
        {mobileFiltersOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 lg:hidden"
          >
            <motion.div
              className="absolute inset-0 bg-black/70 backdrop-blur-sm"
              onClick={() => setMobileFiltersOpen(false)}
            />
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "tween", duration: 0.3, ease: [0.2, 0.8, 0.2, 1] }}
              className="absolute bottom-0 inset-x-0 max-h-[92vh] bg-[#0f0f0f] border-t border-white/[0.08] rounded-t-3xl flex flex-col"
            >
              <div className="flex items-center justify-between p-5 border-b border-white/[0.06] shrink-0">
                <div className="flex items-center gap-2">
                  <SlidersHorizontal className="h-5 w-5 text-[#D4AF37]" />
                  <div className="text-lg font-extrabold text-white">
                    Filtre
                  </div>
                  {hasActiveFilters && (
                    <span className="h-6 min-w-6 px-2 rounded-full bg-[#D4AF37] text-[#0a0a0a] text-[11px] font-bold flex items-center justify-center">
                      {activeFilterCount}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setMobileFiltersOpen(false)}
                  className="h-10 w-10 rounded-full border border-white/[0.1] hover:border-[#D4AF37]/50 flex items-center justify-center text-white transition-colors"
                  aria-label="Închide"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-5 overflow-y-auto flex-1">
                <FilterFields getP={getP} setP={setP} brands={cleanBrands} />
              </div>
              <div
                className="p-4 border-t border-white/[0.06] grid grid-cols-2 gap-3 shrink-0 bg-[#0f0f0f]"
                style={{
                  paddingBottom: "max(1rem, env(safe-area-inset-bottom))",
                }}
              >
                <button
                  onClick={reset}
                  className="h-12 rounded-full border border-white/[0.1] text-sm font-bold uppercase tracking-[0.14em] text-white/80 hover:text-[#D4AF37] hover:border-[#D4AF37]/50 transition-colors"
                >
                  Resetează
                </button>
                <button
                  onClick={() => setMobileFiltersOpen(false)}
                  className="h-12 rounded-full bg-[#D4AF37] text-[#0a0a0a] text-sm font-bold uppercase tracking-[0.14em] hover:bg-[#e8c86a] transition-colors"
                >
                  Aplică filtrele
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function EmptyState({ onReset }) {
  return (
    <div className="relative rounded-[22px] border border-white/[0.08] bg-[#111111] px-6 sm:px-10 py-16 sm:py-20 text-center overflow-hidden">
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none opacity-30"
        style={{
          background:
            "radial-gradient(50% 40% at 50% 30%, rgba(212,175,55,0.22) 0%, transparent 60%)",
        }}
      />
      <div className="relative flex flex-col items-center">
        {/* Discrete illustration: magnifier + gold ring */}
        <div className="relative h-24 w-24 flex items-center justify-center mb-6">
          <div className="absolute inset-0 rounded-full border border-[#D4AF37]/30 animate-pulse" />
          <div className="absolute inset-3 rounded-full border border-[#D4AF37]/20" />
          <div className="relative h-14 w-14 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/40 flex items-center justify-center">
            <Search className="h-6 w-6 text-[#D4AF37]" strokeWidth={2.2} />
          </div>
        </div>
        <h3 className="font-display text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
          Niciun rezultat
        </h3>
        <p className="mt-3 text-white/60 max-w-md">
          Nu am găsit niciun autoturism care să corespundă criteriilor
          selectate.
        </p>
        <button
          onClick={onReset}
          className="gold-shine mt-8 inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#e8c86a] text-[#0a0a0a] rounded-full px-7 py-3.5 font-bold text-[12px] uppercase tracking-[0.18em] transition-colors shadow-[0_10px_30px_-8px_rgba(212,175,55,0.5)]"
        >
          <RotateCcw className="h-4 w-4" />
          Resetează filtrele
        </button>
      </div>
    </div>
  );
}
